import type {
    ICreatePurchaseIntent,
    IIntentCheckoutSessionResponse,
    IPurchaseIntentResponse,
} from '@escloud/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { userKeys } from './use-current-user';

import { useApi, useRegion, useTenant } from '@/context';
import { errorIds, logError } from '@/lib/log-error';
import { postActivationHref } from '@/lib/onboarding-funnel';
import { clearPurchaseIntentHint, writePurchaseIntentHint } from '@/lib/purchase-intent-hint';
import { clearSelectedPlan } from '@/lib/selected-plan';
import { rememberTenantRegion } from '@/lib/tenant-region-hints';
import { ApiError } from '@/services/api-client';

/**
 * Cache identity carries the region: the intent lives in ONE regional database
 * and the base URL is not part of TanStack's key.
 */
export const purchaseIntentKeys = {
    all: ['purchase-intents'] as const,
    current: (region: string) => [...purchaseIntentKeys.all, 'current', region] as const,
    detail: (region: string, id: string) => [...purchaseIntentKeys.all, region, id] as const,
    checkoutSession: (region: string, id: string) =>
        [...purchaseIntentKeys.all, region, id, 'checkout-session'] as const,
};

/** A 404 from the intent endpoints is an answer ("nothing to resume"), not a failure. */
export function isProvenNoOpenIntent(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}

export function useCurrentPurchaseIntent(region: string | null, options: { enabled?: boolean } = {}) {
    const api = useApi();
    return useQuery({
        queryKey: purchaseIntentKeys.current(region ?? ''),
        queryFn: () => api.purchaseIntents.getCurrent(region!),
        enabled: !!region && (options.enabled ?? true),
        retry: false,
    });
}

export function usePurchaseIntent(
    region: string | null,
    intentId: string | null,
    options: { refetchInterval?: number | false } = {}
) {
    const api = useApi();
    return useQuery({
        queryKey: purchaseIntentKeys.detail(region ?? '', intentId ?? ''),
        queryFn: () => api.purchaseIntents.get(region!, intentId!),
        enabled: !!region && !!intentId,
        retry: false,
        refetchInterval: options.refetchInterval ?? false,
    });
}

/**
 * The Checkout Session as a QUERY, although the endpoint is a POST: the server
 * replays the same session for the same intent, plan and generation
 * (idempotency key), and replaces an expired one. Reading it as a query lets the
 * account step seed it and the payment page pick it up without a second call,
 * and `invalidateQueries` on this key is the "expired session, new one" path.
 */
export function useIntentCheckoutSession(
    region: string | null,
    intentId: string | null,
    options: { enabled?: boolean } = {}
) {
    const api = useApi();
    return useQuery({
        queryKey: purchaseIntentKeys.checkoutSession(region ?? '', intentId ?? ''),
        queryFn: () => api.purchaseIntents.createCheckoutSession(region!, intentId!),
        enabled: !!region && !!intentId && (options.enabled ?? true),
        retry: false,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
}

export function useCreatePurchaseIntent() {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ region, ...data }: ICreatePurchaseIntent & { region: string }) =>
            api.purchaseIntents.create(region, data),
        onSuccess: (intent: IPurchaseIntentResponse, { region }) => {
            // Record the region before anything else can fail: it is the only
            // thing that lets the next screen (or the next session) find it.
            writePurchaseIntentHint({ region, intentId: intent.id, planId: intent.planId });
            queryClient.setQueryData(purchaseIntentKeys.current(region), intent);
            queryClient.setQueryData(purchaseIntentKeys.detail(region, intent.id), intent);
            // A plan or type change invalidates the previous session server-side.
            queryClient.removeQueries({ queryKey: purchaseIntentKeys.checkoutSession(region, intent.id) });
        },
    });
}

export function useCreateIntentCheckoutSession() {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ region, intentId }: { region: string; intentId: string }) =>
            api.purchaseIntents.createCheckoutSession(region, intentId),
        onSuccess: (session: IIntentCheckoutSessionResponse, { region, intentId }) => {
            queryClient.setQueryData(purchaseIntentKeys.checkoutSession(region, intentId), session);
        },
    });
}

export function useAbandonPurchaseIntent() {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ region, intentId }: { region: string; intentId: string }) =>
            api.purchaseIntents.abandon(region, intentId),
        onSettled: (_data, _error, { region }) => {
            // Settled, not success: a 404/409 means it is gone or completed either way.
            queryClient.removeQueries({ queryKey: purchaseIntentKeys.current(region) });
        },
    });
}

/**
 * What happens the moment a purchase materialised a tenant (free plan on the
 * account step, `activated` on the payment page, or the activating poll).
 * The region hint is recorded first (the claims lag), then a best-effort
 * claims refresh, then the registry invalidation, then the selection, then
 * the funnel stores are cleared and the app opens on `POST_ACTIVATION_ROUTE`
 * (the welcome profile page, ESCLOUD-333). The claims are refreshed before
 * the tenant is selected so the selected tenant is never ahead of the token.
 */
export function useWorkspaceActivated(): (tenantId: string, region: string) => Promise<void> {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { refreshToken } = useRegion();
    const { selectTenant } = useTenant();

    return useCallback(
        async (tenantId: string, region: string) => {
            rememberTenantRegion(tenantId, region);
            try {
                await refreshToken();
            } catch (error) {
                logError(
                    errorIds.TENANT_CREATE_CLAIM_REFRESH_FAILED,
                    'Failed to refresh tenant claims after a purchase activated a workspace',
                    { tenantId, error }
                );
            }
            void queryClient.invalidateQueries({ queryKey: userKeys.tenantsInRegion(region) });
            selectTenant(tenantId);
            queryClient.removeQueries({ queryKey: purchaseIntentKeys.all });
            clearPurchaseIntentHint();
            clearSelectedPlan();
            navigate(postActivationHref(tenantId), { replace: true });
        },
        [navigate, queryClient, refreshToken, selectTenant]
    );
}
